import React, { useState, useEffect, useCallback } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from "@/components/ui/collapsible";
import {
  Database,
  MessageSquare,
  FileJson,
  FileSpreadsheet,
  Loader2,
  RefreshCw,
  Bot,
  Eye,
  Clock,
  ChevronDown,
  Sparkles,
  Shield,
  CheckCircle2,
} from "lucide-react";
import { toast } from "sonner";

// Format relative time
const formatRelativeTime = (timestamp) => {
  if (!timestamp) return "";
  const now = new Date();
  const time = new Date(timestamp);
  const diff = Math.floor((now - time) / 1000);

  if (diff < 60) return "just now";
  if (diff < 3600) return `${Math.floor(diff / 60)}m ago`;
  if (diff < 86400) return `${Math.floor(diff / 3600)}h ago`;
  return `${Math.floor(diff / 86400)}d ago`;
};

// Audit event type configuration (matching AuditSystem.jsx pattern)
const AUDIT_TYPE_CONFIG = {
  llm_conversation: { icon: Bot, color: "text-violet-500", bg: "bg-violet-500/10", border: "border-violet-500/20", label: "AI Query" },
  resource_access: { icon: Database, color: "text-blue-400", bg: "bg-blue-500/10", border: "border-blue-500/20", label: "Resource Access" },
  data_export: { icon: FileJson, color: "text-emerald-500", bg: "bg-emerald-500/10", border: "border-emerald-500/20", label: "Data Export" },
  default: { icon: Clock, color: "text-muted-foreground", bg: "bg-muted/10", border: "border-muted/20", label: "Event" },
};

const getAuditTypeConfig = (auditType) => AUDIT_TYPE_CONFIG[auditType] || AUDIT_TYPE_CONFIG.default;

// Event Row Component (matching AuditSystem.jsx pattern)
function DataEventRow({ event }) {
  const config = getAuditTypeConfig(event.audit_type);
  const Icon = config.icon;

  return (
    <div className={`flex items-center gap-3 p-3 rounded-lg ${config.bg} border ${config.border}`}>
      <div className="p-2 rounded-lg bg-[var(--n-bg)]">
        <Icon className={`w-4 h-4 ${config.color}`} />
      </div>
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2">
          <span className="text-sm font-medium">{config.label}</span>
          <Badge variant="outline" className={`text-[10px] ${config.border} ${config.color}`}>
            {event.audit_type || "event"}
          </Badge>
        </div>
        <p className="text-xs text-muted-foreground truncate">
          {event.actor?.email || "System"}
        </p>
      </div>
      <div className="text-right shrink-0">
        <p className="text-xs text-muted-foreground">{formatRelativeTime(event.timestamp)}</p>
        <CheckCircle2 className="w-3.5 h-3.5 text-emerald-500 ml-auto mt-1" />
      </div>
    </div>
  );
}

/**
 * Data Audit Component (Tier 3)
 * 
 * Customer-owned audit data stored in the DATA PLANE.
 * Location: niyant_data.env_{environment_id}
 * 
 * Includes:
 * - LLM conversation history
 * - Resource access logs
 * - GDPR compliant export (JSON/CSV)
 * 
 * Retention: 1 year (configurable)
 */
export default function DataAuditTab({ api, environmentId, environmentName }) {
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [summary, setSummary] = useState(null);
  const [conversations, setConversations] = useState([]);
  const [events, setEvents] = useState([]);
  const [expandedConvo, setExpandedConvo] = useState(null);
  const [exporting, setExporting] = useState(false);

  const fetchData = useCallback(async (showRefresh = false) => {
    if (!environmentId) return;
    if (showRefresh) setRefreshing(true);
    else setLoading(true);

    try {
      const [summaryRes, convosRes, eventsRes] = await Promise.all([
        api().get(`/audit/data/${environmentId}/summary`).catch(() => ({ data: {} })),
        api().get(`/audit/data/${environmentId}/llm-conversations?limit=10`).catch(() => ({ data: { conversations: [] } })),
        api().get(`/audit/data/${environmentId}?limit=20`).catch(() => ({ data: { events: [] } })),
      ]);

      setSummary(summaryRes.data);
      setConversations(convosRes.data.conversations || []);
      setEvents(eventsRes.data.events || []);
    } catch (err) {
      console.error("Failed to load data audit:", err);
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  }, [api, environmentId]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const handleExport = async (format) => {
    setExporting(true);
    try {
      const response = await api().get(`/audit/data/${environmentId}/export?format=${format}`, {
        responseType: 'blob'
      });

      const blob = new Blob([response.data]);
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `data_audit_${environmentId}_${new Date().toISOString().slice(0, 10)}.${format}`;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);

      toast.success(`Data exported as ${format.toUpperCase()}`);
    } catch (err) {
      toast.error("Export failed");
      console.error("Export error:", err);
    } finally {
      setExporting(false);
    }
  };

  if (loading) {
    return (
      <div className="space-y-4">
        {[...Array(2)].map((_, i) => (
          <Card key={i} className="bg-[var(--n-card)] border-[var(--n-border-2)]">
            <CardContent className="p-6"><div className="h-24 bg-[var(--n-border-2)] rounded animate-pulse" /></CardContent>
          </Card>
        ))}
      </div>
    );
  }

  const totalEvents = summary?.totals?.all_time || 0;
  const llmConversations = summary?.llm_usage?.conversations || 0;
  const totalTokens = summary?.llm_usage?.total_tokens || 0;

  return (
    <div className="space-y-4" data-testid={`data-audit-${environmentId}`}>
      {/* Header Card */}
      <Card className="bg-[var(--n-card)] border-[var(--n-border-2)]">
        <CardHeader className="pb-3">
          <div className="flex items-center justify-between">
            <CardTitle className="text-sm font-medium flex items-center gap-2">
              <Database className="w-4 h-4 text-violet-500" />
              Data Audit
              <Badge variant="outline" className="text-[10px] ml-2">Tier 3</Badge>
            </CardTitle>
            <div className="flex items-center gap-2">
              <Button
                variant="ghost"
                size="sm"
                onClick={() => fetchData(true)}
                disabled={refreshing}
                className="h-8 px-2"
                data-testid="refresh-data-audit"
              >
                <RefreshCw className={`w-3.5 h-3.5 ${refreshing ? "animate-spin" : ""}`} />
              </Button>
              <Button
                variant="outline"
                size="sm"
                onClick={() => handleExport('json')}
                disabled={exporting || totalEvents === 0}
                className="h-8 text-xs border-[var(--n-border-2)]"
                data-testid="export-json"
              >
                {exporting ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <FileJson className="w-3.5 h-3.5 mr-1" />}
                JSON
              </Button>
              <Button
                variant="outline"
                size="sm"
                onClick={() => handleExport('csv')}
                disabled={exporting || totalEvents === 0}
                className="h-8 text-xs border-[var(--n-border-2)]"
                data-testid="export-csv"
              >
                <FileSpreadsheet className="w-3.5 h-3.5 mr-1" />
                CSV
              </Button>
            </div>
          </div>
        </CardHeader>
        <CardContent className="space-y-4">
          {/* Info Banner */}
          <div className="flex items-start gap-3 p-3 rounded-lg bg-violet-500/5 border border-violet-500/20">
            <Eye className="w-4 h-4 text-violet-500 shrink-0 mt-0.5" />
            <div>
              <p className="text-sm font-medium text-violet-500">Your Data, Your Control</p>
              <p className="text-xs text-muted-foreground mt-0.5">
                Customer-owned data stored in Data Plane. Export anytime for GDPR compliance. 1 year retention.
              </p>
            </div>
          </div>

          {/* Stats */}
          <div className="grid grid-cols-3 gap-3">
            <div className="p-3 rounded-lg bg-[var(--n-bg)] border border-[var(--n-border-2)]">
              <div className="flex items-center gap-2 mb-1">
                <Database className="w-3.5 h-3.5 text-muted-foreground" />
                <span className="text-[10px] text-muted-foreground uppercase tracking-wider">Total Events</span>
              </div>
              <span className="text-xl font-semibold">{totalEvents}</span>
            </div>

            <div className="p-3 rounded-lg bg-[var(--n-bg)] border border-[var(--n-border-2)]">
              <div className="flex items-center gap-2 mb-1">
                <MessageSquare className="w-3.5 h-3.5 text-violet-400" />
                <span className="text-[10px] text-muted-foreground uppercase tracking-wider">AI Conversations</span>
              </div>
              <span className="text-xl font-semibold">{llmConversations}</span>
            </div>

            <div className="p-3 rounded-lg bg-[var(--n-bg)] border border-[var(--n-border-2)]">
              <div className="flex items-center gap-2 mb-1">
                <Sparkles className="w-3.5 h-3.5 text-amber-400" />
                <span className="text-[10px] text-muted-foreground uppercase tracking-wider">Tokens Used</span>
              </div>
              <span className="text-xl font-semibold">{totalTokens.toLocaleString()}</span>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* LLM Conversations */}
      {conversations.length > 0 && (
        <Card className="bg-[var(--n-card)] border-[var(--n-border-2)]">
          <CardHeader className="pb-3">
            <CardTitle className="text-sm font-medium flex items-center gap-2">
              <Bot className="w-4 h-4 text-violet-500" />
              AI Conversations
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-2">
            {conversations.map((convo, i) => (
              <Collapsible
                key={convo.id || i}
                open={expandedConvo === convo.id}
                onOpenChange={() => setExpandedConvo(expandedConvo === convo.id ? null : convo.id)}
              >
                <CollapsibleTrigger className="flex items-center justify-between w-full p-3 rounded-lg bg-[var(--n-bg)] border border-[var(--n-border-2)] hover:border-[#2a2a30] transition-colors text-left">
                  <div className="flex items-center gap-2 min-w-0">
                    <MessageSquare className="w-4 h-4 text-violet-400 shrink-0" />
                    <span className="text-sm truncate">
                      {convo.prompt?.slice(0, 50) || "AI Query"}...
                    </span>
                  </div>
                  <div className="flex items-center gap-2 shrink-0">
                    <span className="text-[10px] text-muted-foreground">
                      {formatRelativeTime(convo.timestamp)}
                    </span>
                    <ChevronDown className={`w-4 h-4 text-muted-foreground transition-transform ${expandedConvo === convo.id ? "rotate-180" : ""}`} />
                  </div>
                </CollapsibleTrigger>

                <CollapsibleContent className="mt-2">
                  <div className="pl-2 border-l-2 border-[var(--n-border-2)] ml-2 space-y-3 py-2">
                    <div>
                      <p className="text-[10px] text-muted-foreground uppercase tracking-wider mb-1">Prompt</p>
                      <p className="text-sm text-foreground/80 p-2 rounded bg-[var(--n-bg)] border border-[var(--n-border-2)]">
                        {convo.prompt || "No prompt recorded"}
                      </p>
                    </div>
                    <div>
                      <p className="text-[10px] text-muted-foreground uppercase tracking-wider mb-1">Response</p>
                      <p className="text-sm text-foreground/80 p-2 rounded bg-[var(--n-bg)] border border-[var(--n-border-2)] max-h-40 overflow-y-auto">
                        {convo.response || "No response recorded"}
                      </p>
                    </div>
                    {convo.metadata?.tokens && (
                      <div className="flex items-center gap-2 text-xs text-muted-foreground">
                        <Sparkles className="w-3 h-3" />
                        {convo.metadata.tokens} tokens
                      </div>
                    )}
                  </div>
                </CollapsibleContent>
              </Collapsible>
            ))}
          </CardContent>
        </Card>
      )}

      {/* Recent Events */}
      <Card className="bg-[var(--n-card)] border-[var(--n-border-2)]">
        <CardHeader className="pb-3">
          <div className="flex items-center justify-between">
            <CardTitle className="text-sm font-medium flex items-center gap-2">
              <Clock className="w-4 h-4 text-[var(--hig-accent)]" />
              Recent Events
            </CardTitle>
            <span className="text-xs text-muted-foreground">
              1 year retention • Data Plane
            </span>
          </div>
        </CardHeader>
        <CardContent>
          {events.length === 0 ? (
            <div className="py-8 text-center">
              <Shield className="w-10 h-10 text-muted-foreground mx-auto mb-3" />
              <h3 className="text-sm font-medium">No data audit events yet</h3>
              <p className="text-xs text-muted-foreground mt-1 max-w-sm mx-auto">
                Events appear here as you use AI features and access resources in this environment
              </p>
            </div>
          ) : (
            <div className="space-y-2">
              {events.map((event, i) => (
                <DataEventRow key={event.id || i} event={event} />
              ))}
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
